import { jsx, jsxs } from "react/jsx-runtime";
import { PromptInput, PromptInputBody, PromptInputTextarea } from "../../../ai-elements/prompt-input.js";
import { ChatInput } from "@brandclub/bc-widgets";
import { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { AttachmentsRow } from "./AttachmentsRow.js";
import { AttachmentButton } from "./AttachmentButton.js";
import { MicrophoneButton } from "./MicrophoneButton.js";
import { SubmitButton } from "./SubmitButton.js";
import { buildPromptInputStyles } from "./styles.js";
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const PromptInputConcierge = ({
  placeholder = "Ask anything",
  onSubmit,
  disabled = false,
  isStreaming = false,
  onStop,
  value,
  onChange,
  expandDirection = "up",
  maxHeight,
  widgetId = "concierge-chat-input",
  displayName = "Chat Input"
}) => {
  const textareaRef = useRef(null);
  const [hasAttachments, setHasAttachments] = useState(false);
  const isMultiline = ((value == null ? void 0 : value.length) ?? 0) > 40 || ((value == null ? void 0 : value.includes("\n")) ?? false);
  const handleSubmit = useCallback(
    async (message) => {
      var _a;
      if (!((_a = message.text) == null ? void 0 : _a.trim()) && (!message.files || message.files.length === 0)) {
        return;
      }
      const fileParts = [];
      if (message.files && message.files.length > 0) {
        for (const file of message.files) {
          if (!ACCEPTED_IMAGE_TYPES.includes(file.mediaType || "")) {
            toast.error("Please upload a valid image file (JPEG, PNG, WebP, or GIF)");
            return;
          }
          if (file.url) {
            if (file.url.startsWith("data:") || file.url.startsWith("http")) {
              fileParts.push({ type: "file", mediaType: file.mediaType || "image/jpeg", url: file.url });
            } else if (file.url.startsWith("blob:")) {
              try {
                const resp = await fetch(file.url);
                const blob = await resp.blob();
                const dataUrl = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
                fileParts.push({ type: "file", mediaType: file.mediaType || blob.type || "image/jpeg", url: dataUrl });
              } catch (error) {
                console.error("Failed to read blob URL:", error);
                toast.error("Failed to read image file");
                return;
              }
            }
          }
        }
      }
      onSubmit(message.text || "", fileParts.length > 0 ? fileParts : void 0);
    },
    [onSubmit]
  );
  const handleError = useCallback((err) => {
    if (err.code === "max_file_size") {
      toast.error("File size exceeds 10MB limit");
    } else if (err.code === "accept") {
      toast.error("Please upload a valid image file");
    } else {
      toast.error(err.message);
    }
  }, []);
  return /* @__PURE__ */ jsx(ChatInput, { widgetId, displayName, children: (styles, themeIcons, resolveColor) => {
    const isExpanded = isMultiline || hasAttachments;
    const borderRadius = isExpanded ? `${styles.borderRadiusMultiLine}px` : `${styles.borderRadiusSingleLine}px`;
    const s = styles;
    return /* @__PURE__ */ jsx("div", { className: "tw:mx-auto tw:w-full", style: { maxWidth: `${styles.maxWidth}px` }, children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-prompt-input",
        style: {
          borderRadius,
          border: `${styles.borderWidth}px solid ${resolveColor(styles.borderColor)}`,
          backgroundColor: resolveColor(styles.backgroundColor),
          boxShadow: styles.shadowEnabled ? styles.shadow : "none",
          transition: "border-radius 0.15s ease-out"
        },
        children: [
          /* @__PURE__ */ jsx("style", { children: buildPromptInputStyles({
            focusBorderColor: resolveColor(styles.borderColorFocus),
            fontSize: styles.fontSize,
            fontWeight: styles.fontWeight,
            fontFamilyCss: s.fontFamilyCss || "inherit",
            textColor: resolveColor(styles.textColor),
            placeholderColor: resolveColor(styles.placeholderColor),
            submitBgColor: resolveColor(styles.submitButtonBgColor),
            submitBgColorHover: resolveColor(styles.submitButtonBgColorHover),
            mobileFontSize: s.mobile_fontSize ?? styles.fontSize,
            mobilePaddingY: s.mobile_paddingY ?? styles.paddingY,
            mobilePaddingX: s.mobile_paddingX ?? styles.paddingX,
            mobileMinHeight: s.mobile_minHeight ?? s.minHeight ?? 0
          }) }),
          /* @__PURE__ */ jsx(
            PromptInput,
            {
              onSubmit: handleSubmit,
              multiple: true,
              globalDrop: true,
              accept: "image/*",
              maxFiles: 5,
              maxFileSize: MAX_FILE_SIZE,
              onError: handleError,
              className: "tw:w-full",
              children: /* @__PURE__ */ jsxs(PromptInputBody, { className: "tw:flex tw:flex-col tw:w-full", children: [
                /* @__PURE__ */ jsx(AttachmentsRow, { onAttachmentsChange: setHasAttachments, styles }),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "tw:flex tw:w-full",
                    style: {
                      flexDirection: isMultiline ? "column" : "row",
                      alignItems: isMultiline ? "stretch" : "flex-end",
                      transition: "all 0.2s ease-out"
                    },
                    children: [
                      /* @__PURE__ */ jsx("div", { className: "tw:flex-1 tw:min-w-0", children: /* @__PURE__ */ jsx(
                        PromptInputTextarea,
                        {
                          ref: textareaRef,
                          placeholder,
                          disabled,
                          value,
                          onChange: (e) => onChange == null ? void 0 : onChange(e.target.value),
                          rows: 1,
                          style: {
                            minHeight: s.minHeight > 0 ? `${s.minHeight}px` : 0,
                            maxHeight: maxHeight ?? Math.ceil(styles.fontSize * 1.5 * 3),
                            overflowY: "auto",
                            paddingTop: styles.paddingY,
                            paddingBottom: isMultiline ? 4 : styles.paddingY,
                            paddingLeft: styles.paddingX,
                            paddingRight: isMultiline ? styles.paddingX : void 0,
                            transition: "padding 0.2s ease-out"
                          }
                        }
                      ) }),
                      /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "tw:flex tw:items-center tw:shrink-0 concierge-prompt-input-icons",
                          style: {
                            gap: s.iconGap ?? 8,
                            paddingRight: styles.paddingX,
                            paddingLeft: styles.paddingX,
                            paddingBottom: styles.paddingY,
                            justifyContent: isMultiline ? "flex-end" : void 0,
                            transition: "all 0.2s ease-out"
                          },
                          children: [
                            styles.showAttachmentButton && /* @__PURE__ */ jsx(
                              AttachmentButton,
                              {
                                disabled: disabled || isStreaming,
                                styles,
                                resolveColor
                              }
                            ),
                            styles.showMicrophoneButton && /* @__PURE__ */ jsx(
                              MicrophoneButton,
                              {
                                disabled: disabled || isStreaming,
                                textareaRef,
                                onChange,
                                styles,
                                themeIcons,
                                resolveColor
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              SubmitButton,
                              {
                                disabled: disabled || !isStreaming && !(value == null ? void 0 : value.trim()) && !hasAttachments,
                                isStreaming,
                                onStop,
                                styles,
                                resolveColor
                              }
                            )
                          ]
                        }
                      )
                    ]
                  }
                )
              ] })
            }
          )
        ]
      }
    ) });
  } });
};
export {
  PromptInputConcierge
};
//# sourceMappingURL=index.js.map
